<template>
  <!-- $getWebImage(images, 'main/configuration/background/origin') || -->
  <main
    id="app"
    class="lead min-vh-100 bg-image-full"
    :style="
      theme !== 'a11y'
        ? `background-image: url(${$getImage('main-bg.png', 'backgrounds')})`
        : 'background: #212121'
    "
  >
    <Authorized>
      <wiki-map>
        <audio-player @sendNewCommand="sendNewCommand" ref="audio-player">
          <main class="flex-grow-1 d-flex flex-column">
            <component
              :is="layout"
              @openAudioPlauer="isPlayAudio()"
              @slidebarOpen="openAudioPlayers()"
              @start="startconf(), myVirtualRooma(), gpsCoordsDefinition()"
              @tvconnect="sendNewCommand"
              @tvlist="loadAvailableTvs"
            />
          </main>
        </audio-player>
      </wiki-map>
    </Authorized>
    <tv-selector
      ref="TvSelector"
      :televisions="available_tvs"
      @selectedTv="wsSelectedTv"
    ></tv-selector>
    <MagicRemoteJoystick ref="MagicRemoteJoystick" @sendNewCommand="sendNewCommand" />
    <Notifications />
  </main>
</template>
<script lang="ts">
import { Options, mixins } from "vue-class-component";
import { Notifications } from "@kyvg/vue3-notification";
import { theme } from "@/store/modules/theme";
import { auth } from "./config/host.config";
import { useVRoom } from "./store/modules/hotel/virtual-room";
import { useTVConnect } from "./store/modules/media/tv-connect";
import { AccessToken } from "./api/accessDecoder";
import {
  scheduleProactiveRefresh,
  refreshAccessToken,
} from "@/api/authorization/auth-request-api";
import {
  startConnectivitySupervisor,
  onServerRestored,
  isServerReachable,
} from "@/services/connectivity";

import type { RouteMeta } from "vue-router";

import MainLayout from "@/components/layouts/MainLayout.vue";
import EmptyLayout from "@/components/layouts/EmptyLayout.vue";
import DashboardLayout from "@/components/layouts/DashboardLayout.vue";
import Authorized from "@/components/modules/Authorized.vue";
import WikiMap from "./modules/wikimap/wiki-map";
import AudioPlayer from "./modules/media/audio-player.vue";
import TvSelector from "@/modules/magic_remote/components/tv-selector.vue";
import MagicRemoteJoystick from "./components/magic_remote/magic-remote-joystick.vue";

import Configuration from "@/modules/configuration/configuration";
import Coordinates from "@/modules/geoposition/coordinates";
import Connect from "@/modules/magic_remote/Connect";

interface CustomRouteMeta extends RouteMeta {
  layout?: string;
}

@Options({
  name: "app",
  components: {
    Authorized,
    MainLayout,
    EmptyLayout,
    DashboardLayout,
    WikiMap,
    AudioPlayer,
    TvSelector,
    MagicRemoteJoystick,
    Notifications,
  },
  watch: {
    remoteOpenToken() {
      this.$nextTick(() => this.$refs.MagicRemoteJoystick?.openOffcanvas());
    },
  },
})
class App extends mixins(Configuration, Coordinates, Connect) {
  private weatherInterval!: ReturnType<typeof setTimeout>;
  private unsubscribeRestored: (() => void) | null = null;
  useTheme = theme();

  $route!: { meta?: { layout?: string } };
  $refs!: {
    "audio-player": InstanceType<typeof AudioPlayer>;
    TvSelector: InstanceType<typeof TvSelector>;
    MagicRemoteJoystick: InstanceType<typeof MagicRemoteJoystick>;
  };
  get images(): string {
    return this.configurations.getBackground;
  }

  get remoteOpenToken(): number {
    return useTVConnect().getRemoteOpenToken;
  }

  get layout(): string {
    if (!this.$route?.meta) return "main-layout";

    const meta = this.$route.meta as CustomRouteMeta;
    return meta?.layout ? `${meta.layout}-layout` : "main-layout";
  }

  get audioPlayer(): InstanceType<typeof AudioPlayer> {
    return this.$refs["audio-player"];
  }

  get theme(): string {
    return this.useTheme.getTheme;
  }

  created(): void {
    const savedTheme = localStorage.getItem("data-bs-theme");
    const validThemes = ["light", "dark"] as const;
    type Theme = (typeof validThemes)[number];

    const theme: Theme = validThemes.includes(savedTheme as Theme)
      ? (savedTheme as Theme)
      : "dark";

    document.documentElement.setAttribute("data-bs-theme", theme);
    this.useTheme.setTheme(theme);
    this.useTheme.setWidth(window.innerWidth);
    this.onAvailableTVs = this.openTVSelector;
  }

  mounted(): void {
    startConnectivitySupervisor();
    this.unsubscribeRestored = onServerRestored(() => {
      this.handleServerRestored();
    });

    if (AccessToken.access) {
      this.myVirtualRooma();
      scheduleProactiveRefresh();
    }
    window.addEventListener("resize", this.setWidth);
    this.weatherInterval = setInterval(this.myVirtualRooma, this.$second(5));
  }

  beforeMount(): void {
    this.getTokens();
  }

  unmounted(): void {
    window.removeEventListener("resize", this.setWidth);
    if (this.weatherInterval) clearInterval(this.weatherInterval);
    this.unsubscribeRestored?.();
    this.unsubscribeRestored = null;
  }

  /**
   * Сервер вернулся в строй после простоя (или вкладка «проснулась»):
   * обновляем токен, WS и данные, а не ждём ближайшего тика интервала.
   */
  async handleServerRestored(): Promise<void> {
    if (!AccessToken.isAuthorized) return;

    // За время простоя access-токен мог истечь — обновляем заранее
    if (!AccessToken.isValid) {
      try {
        await refreshAccessToken();
      } catch {
        return; // 400/401 → выход уже выполнен внутри refreshAccessToken
      }
    }

    if (!this.socket?.connected) this.connect();

    this.myVirtualRooma();
    this.createConfiguration();
    this.createHotelConfig();
    this.createInformelList();
    this.crealeCurrencyList();
  }

  setWidth(): void {
    this.useTheme.setWidth(window.innerWidth);
  }

  getTokens(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const refreshToken = urlParams.get("refreshToken");
    const accessToken = urlParams.get("access");
    if (refreshToken) localStorage.setItem("refreshToken", refreshToken);
    if (accessToken) localStorage.setItem("access", accessToken);
  }

  isPlayAudio(): void {
    if (!this.audioPlayer) return;
    this.$refs["audio-player"].playAudio();
  }

  openAudioPlayers(): void {
    if (!this.audioPlayer) return;
    this.$refs["audio-player"].slidebarOpen();
  }

  openTVSelector(): void {
    this.$refs.TvSelector.openOffcanvas();
  }

  async myVirtualRooma(): Promise<void> {
    if (!isServerReachable()) return;
    try {
      const res = await this.$axios.get(auth.myrooms);
      if (res.status === 200) {
        useVRoom().setRvooma(res.data);
      }
    } catch (e) {
      this.$error(e);
    }
  }
}

export default App;
</script>
<style lang="scss">
// * {
//   outline: 1px solid salmon !important;
// }
</style>
